import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

import {
  CaretDown,
  CaretUp,
  CheckCircle,
  Circle,
  Sparkle,
  X
} from '@phosphor-icons/react';
const GettingStartedChecklist = ({ user, stats }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has dismissed the checklist
    const dismissed = localStorage.getItem('checklist_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Calculate checklist completion
  const checks = {
    profile: user?.name && user.name !== 'User' && user.name.trim().length > 0,
    firstAssessment: stats?.total_assessments > 0,
    firstProcess: stats?.total_processes > 0,
    exploredOpportunities: localStorage.getItem('viewed_opportunities') === 'true'
  };

  const completedCount = Object.values(checks).filter(Boolean).length;
  const totalCount = 4;
  const progress = (completedCount / totalCount) * 100;

  // Fire confetti when all tasks complete
  useEffect(() => {
    if (completedCount === totalCount && !localStorage.getItem('checklist_confetti_shown')) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      localStorage.setItem('checklist_confetti_shown', 'true');
    }
  }, [completedCount, totalCount]);

  const handleDismiss = () => {
    localStorage.setItem('checklist_dismissed', 'true');
    setIsDismissed(true);
  };

  if (isDismissed || completedCount === totalCount) {
    return null;
  }

  const checklistItems = [
    {
      id: 'profile',
      title: 'Complete your profile',
      description: 'Add your details to personalize your experience',
      completed: checks.profile,
      action: () => navigate('/account'),
      buttonText: 'Go to Profile'
    },
    {
      id: 'firstAssessment',
      title: 'Complete your first AI assessment',
      description: 'Discover automation opportunities in your business',
      completed: checks.firstAssessment,
      action: () => navigate('/assessments/wizard'),
      buttonText: 'Start Assessment'
    },
    {
      id: 'firstProcess',
      title: 'Map one process',
      description: 'Document a business process to get AI insights',
      completed: checks.firstProcess,
      action: () => navigate('/processes'),
      buttonText: 'Create Process'
    },
    {
      id: 'exploredOpportunities',
      title: 'Explore your opportunities',
      description: 'Review AI-generated automation recommendations',
      completed: checks.exploredOpportunities,
      action: () => navigate('/my-assessments'),
      buttonText: 'View Assessments'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="mb-8 glass rounded-3xl p-6 relative overflow-hidden"
    >
      {/* Gradient Border Effect */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899] opacity-20"></div>
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <Sparkle size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Getting Started</h3>
              <p className="text-sm text-[#A0AEC0]">
                {completedCount} of {totalCount} steps completed
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-[#718096] hover:text-white transition-colors p-2"
            >
              {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
            </button>
            <button
              onClick={handleDismiss}
              className="text-[#718096] hover:text-white transition-colors p-2"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="h-2 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"
            />
          </div>
        </div>

        {/* Checklist Items */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-3"
            >
              {checklistItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.08)] transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {item.completed ? (
                      <CheckCircle size={24} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                    ) : (
                      <Circle size={24} className="text-[#718096] flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <h4 className={`font-semibold ${item.completed ? 'text-[#14F195] line-through' : 'text-white'}`}>
                        {item.title}
                      </h4>
                      <p className="text-sm text-[#A0AEC0]">{item.description}</p>
                    </div>
                  </div>
                  
                  {!item.completed && (
                    <button
                      onClick={item.action}
                      className="ml-4 px-4 py-2 rounded-full bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white text-sm font-semibold hover:opacity-90 transition-opacity flex-shrink-0"
                    >
                      {item.buttonText}
                    </button>
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default GettingStartedChecklist;
